import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Clock, ArrowLeft, Share2, Linkedin, Twitter } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const bgColor = '#1a2332';
  const accentColor = '#d4af37';

  useEffect(() => {
    loadPost();
  }, [slug]);

  const loadPost = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/blog/posts/${slug}`);
      
      if (!response.ok) {
        throw new Error('Post not found');
      }
      
      const data = await response.json();
      setPost(data);
    } catch (error) {
      console.error('Error loading post:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const handleShare = (platform) => {
    const url = window.location.href;
    const text = `Check out this article: ${post.title}`;
    
    if (platform === 'linkedin') {
      window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
    } else if (platform === 'twitter') {
      window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`, '_blank');
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    alert('Link copied to clipboard!');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: bgColor }}>
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: bgColor }}>
        <Header />
        <div className="container mx-auto px-6 py-20 text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Post Not Found</h1>
          <p className="text-white/70 mb-8">The article you&apos;re looking for doesn&apos;t exist.</p>
          <Button onClick={() => navigate('/blog')} style={{ backgroundColor: accentColor, color: bgColor }}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Blog
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: bgColor }}>
      <SEOHead 
        title={`${post.title} | Venkat Swaroop Blog`}
        description={post.excerpt}
        keywords={`${post.category}, ${post.tags?.join(', ') || ''}, Venkat Swaroop, talent acquisition, HR insights`}
        image={post.featured_image}
        url={`https://venkatswaroop.in/blog/${slug}`}
        type="article"
        article={{
          title: post.title,
          excerpt: post.excerpt,
          author: post.author_name,
          publishedDate: post.published_date || post.created_at,
          modifiedDate: post.updated_at,
          category: post.category,
          tags: post.tags,
          image: post.featured_image
        }}
      />
      <Header />
      
      <div className="container mx-auto px-6 pt-24 pb-12">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/blog')}
          className="text-white hover:bg-white/10 mb-8"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Articles
        </Button>

        {/* Article Header */}
        <div className="max-w-4xl mx-auto mb-12">
          <div className="flex items-center gap-3 mb-6">
            <Badge style={{ backgroundColor: accentColor, color: bgColor }}>
              {post.category}
            </Badge>
            {post.tags && post.tags.map((tag, index) => (
              <Badge key={index} variant="outline" className="text-white border-white/30">
                {tag}
              </Badge>
            ))}
          </div>

          <h1 className="font-serif text-4xl md:text-5xl font-bold text-white mb-6">
            {post.title}
          </h1>

          <div className="flex flex-wrap items-center gap-6 text-white/70 mb-8">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              <span>{post.read_time} min read</span>
            </div>
          </div>

          {/* Author Info */}
          <div className="flex items-center gap-4 pb-6 border-b border-white/20">
            <div>
              <p className="text-white font-semibold">{post.author_name}</p>
              <p className="text-white/60 text-sm">{post.author_title}</p>
            </div>
          </div>
        </div>

        {/* Featured Image */}
        {post.featured_image && (
          <div className="max-w-4xl mx-auto mb-12">
            <img 
              src={post.featured_image} 
              alt={post.title}
              className="w-full h-96 object-cover rounded-lg"
            />
          </div>
        )}

        {/* Article Content */}
        <div className="max-w-4xl mx-auto">
          <Card className="border-none bg-white/95 shadow-xl mb-12">
            <CardContent className="p-8 md:p-12">
              <div className="prose prose-slate prose-lg max-w-none
                prose-headings:font-serif prose-headings:text-slate-900 prose-headings:font-bold
                prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6 prose-h2:pb-3 prose-h2:border-b prose-h2:border-slate-200
                prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-4 prose-h3:text-slate-800
                prose-h4:text-xl prose-h4:mt-6 prose-h4:mb-3 prose-h4:text-slate-700
                prose-p:text-slate-700 prose-p:leading-relaxed prose-p:mb-6 prose-p:text-lg
                prose-a:text-amber-600 prose-a:no-underline hover:prose-a:underline prose-a:font-medium
                prose-strong:text-slate-900 prose-strong:font-semibold
                prose-ul:text-slate-700 prose-ul:my-6 prose-ul:space-y-2
                prose-ol:text-slate-700 prose-ol:my-6 prose-ol:space-y-2
                prose-li:text-slate-700 prose-li:leading-relaxed
                prose-li:marker:text-amber-600
                prose-blockquote:border-l-4 prose-blockquote:border-l-amber-500 prose-blockquote:bg-amber-50 prose-blockquote:py-4 prose-blockquote:px-6 prose-blockquote:my-6 prose-blockquote:text-slate-700 prose-blockquote:italic
                prose-code:text-amber-700 prose-code:bg-amber-50 prose-code:px-2 prose-code:py-1 prose-code:rounded prose-code:text-sm
                prose-pre:bg-slate-100 prose-pre:border prose-pre:border-slate-300 prose-pre:p-4 prose-pre:rounded-lg">
                <ReactMarkdown>
                  {post.content}
                </ReactMarkdown>
              </div>
            </CardContent>
          </Card>

          {/* Share Section */}
          <Card className="border-none bg-white/95 shadow-xl mb-12">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <h3 className="text-slate-900 font-semibold flex items-center gap-2">
                  <Share2 className="h-5 w-5 text-amber-600" />
                  Share this article
                </h3>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleShare('linkedin')}
                    className="text-slate-700 border-slate-300 hover:bg-slate-100 hover:border-amber-500 hover:text-amber-600"
                  >
                    <Linkedin className="h-4 w-4 mr-2" />
                    LinkedIn
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleShare('twitter')}
                    className="text-slate-700 border-slate-300 hover:bg-slate-100 hover:border-amber-500 hover:text-amber-600"
                  >
                    <Twitter className="h-4 w-4 mr-2" />
                    Twitter
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopyLink}
                    className="text-slate-700 border-slate-300 hover:bg-slate-100 hover:border-amber-500 hover:text-amber-600"
                  >
                    Copy Link
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BlogPostPage;
